import jwtDecode from 'jwt-decode';
import apiClient from 'api';
import { login, logout, mfaAuth, cancelMfa } from 'api/auth';
import { fetchAuthenticatedUser, editAuthenticatedUser } from 'api/users';
import { get } from 'lodash';
import { navigate } from '@reach/router';
import { REQUEST_FAIL, REQUEST_SUCCESS } from 'reducers/modal/actions';

export const LOGIN_SUCCESS = 'LOGIN_SUCCESS';
export const LOGIN_FAIL = 'LOGIN_FAIL';
export const MFA_SUCCESS = 'MFA_SUCCESS';
export const MFA_FAIL = 'MFA_FAIL';
export const REFRESH_AUTHENTICATED_USER_INFO_SUCCESS =
  'REFRESH_AUTHENTICATED_USER_INFO_SUCCESS';
export const REFRESH_AUTHENTICATED_USER_INFO_FAIL =
  'REFRESH_AUTHENTICATED_USER_INFO_FAIL';

export const SET_AUTHENTICATED_USER_INFO_SUCCESS =
  'SET_AUTHENTICATED_USER_INFO_SUCCESS';
export const SET_AUTHENTICATED_USER_INFO_FAIL =
  'SET_AUTHENTICATED_USER_INFO_FAIL';

export const LOGOUT_SUCCESS = 'LOGOUT_SUCCESS';
export const LOGOUT_FAIL = 'LOGOUT_FAIL';
export const SET_MFA_ERROR_MESSAGE = 'SET_MFA_ERROR_MESSAGE';
export const EDIT_ACCOUNT_INFO_SUCCESS = 'EDIT_ACCOUNT_INFO_SUCCESS';

export const authenticateUser = (email, password) => async dispatch => {
  try {
    const response = await login(email, password);
    const mfaEnabled = window.env.REACT_APP_MFA_ENABLED === 'true';

    if (mfaEnabled && response && response.mfaAuthToken) {
      return { mfaAuthToken: response.mfaAuthToken };
    } else if (!mfaEnabled && response && response.token) {
      const token = response.token;
      const decodedToken = jwtDecode(token);
      const user = decodedToken.user;
      apiClient.setAuthToken(token);
      const userInfo = await fetchAuthenticatedUser();

      await dispatch({
        type: LOGIN_SUCCESS,
        data: { user: userInfo, userToken: token }
      });

      return user;
    } else {
      throw new Error('Unexpected response format');
    }
  } catch (err) {
    await dispatch({
      type: LOGIN_FAIL,
      err
    });
    throw err;
  }
};

export const authenticateMFA =
  (email, mfaAuthToken, mfaToken, url) => async dispatch => {
    try {
      const { token } = await mfaAuth(email, mfaAuthToken, mfaToken);
      const decodedToken = jwtDecode(token);
      const user = decodedToken.user;
      apiClient.setAuthToken(token);
      const userInfo = await fetchAuthenticatedUser();
      await dispatch({
        type: MFA_SUCCESS,
        data: { user: userInfo, userToken: token }
      });
      navigate(url, { state: null });
      return user;
    } catch (err) {
      await dispatch({
        type: MFA_FAIL,
        err
      });
      throw err;
    }
  };

export const cancelMfaRequest = (email, mfaAuthToken) => async dispatch => {
  const MFA_CANCEL_SUCCESS = 'MFA request was successfully canceled.';
  const MFA_CANCEL_FAIL =
    'MFA request could not be canceled. Please try again.';

  try {
    const result = await cancelMfa(email, mfaAuthToken);
    await dispatch({
      type: REQUEST_SUCCESS,
      message: MFA_CANCEL_SUCCESS
    });
    return result;
  } catch (err) {
    dispatch({
      type: REQUEST_FAIL,
      message: MFA_CANCEL_FAIL
    });
    return err;
  }
};

export const refreshAuthenticatedUserInfo = userToken => async dispatch => {
  try {
    const decodedToken = jwtDecode(userToken);
    const user = decodedToken.user;
    apiClient.setAuthToken(userToken);

    if (!get(user, 'role')) {
      return dispatch({
        type: REFRESH_AUTHENTICATED_USER_INFO_FAIL,
        err: 'Invalid token'
      });
    }
    const userInfo = await fetchAuthenticatedUser();
    return dispatch({
      type: REFRESH_AUTHENTICATED_USER_INFO_SUCCESS,
      data: { user: userInfo, userToken }
    });
  } catch (err) {
    dispatch({
      type: REFRESH_AUTHENTICATED_USER_INFO_FAIL,
      err
    });
    throw err;
  }
};

export const setAuthenticatedUserInfo = userInfo => async dispatch => {
  try {
    return dispatch({
      type: SET_AUTHENTICATED_USER_INFO_SUCCESS,
      data: userInfo
    });
  } catch (err) {
    dispatch({
      type: SET_AUTHENTICATED_USER_INFO_FAIL,
      err
    });
    throw err;
  }
};

export const logoutUser =
  showExpirationWarning => async (dispatch, getState) => {
    try {
      if (showExpirationWarning) {
        dispatch({
          type: REQUEST_FAIL,
          message: 'Your session has expired. Please log in again'
        });
      } else {
        await logout();
      }

      return await dispatch({
        type: LOGOUT_SUCCESS
      });
    } catch (err) {
      dispatch({
        type: LOGOUT_FAIL,
        err
      });
      throw err;
    }
  };

export const editAccountInfo =
  updatedAccountInfo => async (dispatch, getState) => {
    try {
      const authenticatedUser = getState().auth.authenticatedUser;
      const updatedUserInfo = await editAuthenticatedUser(
        authenticatedUser.id,
        updatedAccountInfo
      );

      await dispatch({
        type: EDIT_ACCOUNT_INFO_SUCCESS,
        data: updatedUserInfo
      });
    } catch (err) {
      // No error modal because the component that uses this handles errors in its own way
      // Should be made consistent in the future
      throw err;
    }
  };
